import type { AudioPlaybackState } from './audio-playback-manager';
import { AudioPlaybackManager } from './audio-playback-manager';
import { AudioAnalyserService } from './audio-analyser.service';
import { AnalyticsEvent } from '../constants/analyticsEvents';
import type { BaseStreamEvent } from './chat-api.service';
import type { Citation, DisplayFilterGroup, SuggestedCTA } from './types';
import {
  SentenceQueue,
  consumeEventStream,
  SpeculativePipelineManager,
} from './speculative-pipeline';

export type VoiceModeState = 'off' | 'listening' | 'processing' | 'speaking';

export interface VoiceModeCallbacks {
  stt: {
    startListening: (opts?: { continuous?: boolean }) => void;
    stopListening: () => void;
  };
  chat: {
    sendMessage: (args: {
      newMessage?: string;
      filterGroups?: DisplayFilterGroup[];
    }) => Promise<{ answer?: string } | undefined>;
    getAppliedFilterGroups: () => DisplayFilterGroup[];
    streamVoiceSearch: (question: string) => AsyncGenerator<BaseStreamEvent>;
    displayVoiceExchange: (transcript: string, answer: string) => void;
    updateVoiceAnswer: (answer: string) => void;
    updateVoiceMetadata: (metadata: {
      citations?: Citation[];
      suggestedCTA?: SuggestedCTA | null;
    }) => void;
    startSpeculativeFetch: (
      question: string,
      signal: AbortSignal
    ) => Promise<Response>;
    consumeVoiceStream: (response: Response) => AsyncGenerator<BaseStreamEvent>;
  };
  tts: {
    requestAudio: (text: string) => Promise<Response>;
    isEnabled: boolean;
  };
  analytics: {
    trackEvent: (event: string, data?: Record<string, unknown>) => void;
    /** Persists event to backend (vs trackEvent which is client-side analytics) */
    createEvent: (event: string, data?: Record<string, unknown>) => void;
  };
}

interface VoiceModeSnapshot {
  voiceModeState: VoiceModeState;
  isTtsMuted: boolean;
  isReadingOut: boolean;
}

const MAX_SILENCE_STRIKES = 3;
const MAX_VOICE_TURNS = 20;
// OS audio hardware needs time to switch from speaker output to mic input
const POST_TTS_LISTEN_DELAY_MS = 200;

export class VoiceModeOrchestrator {
  private _state: VoiceModeState = 'off';
  private _isTtsMuted = false;
  private _snapshot: VoiceModeSnapshot = {
    voiceModeState: 'off',
    isTtsMuted: false,
    isReadingOut: false,
  };
  private listeners: Set<() => void> = new Set();
  private silenceStrikes = 0;
  private turnCount = 0;
  private callbacks: VoiceModeCallbacks | null = null;
  private prevAudioState: AudioPlaybackState;
  private unsubscribeAudio: (() => void) | null = null;
  private micStream: MediaStream | null = null;
  private _analyserService = new AudioAnalyserService();
  private reListenTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private speculative = new SpeculativePipelineManager();

  private audioManager: AudioPlaybackManager;

  constructor(audioManager?: AudioPlaybackManager) {
    this.audioManager = audioManager ?? new AudioPlaybackManager();
    this.audioManager.setAnalyserService(this._analyserService);
    this.prevAudioState = this.audioManager.getSnapshot();
    this.unsubscribeAudio = this.audioManager.subscribe(() => {
      const next = this.audioManager.getSnapshot();
      const prev = this.prevAudioState;
      this.prevAudioState = next;
      this.handleAudioStateChange(prev, next);
    });
  }

  // -- useSyncExternalStore contract --

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  getSnapshot(): VoiceModeSnapshot {
    return this._snapshot;
  }

  // -- Public API --

  getAnalyserService(): AudioAnalyserService {
    return this._analyserService;
  }

  updateCallbacks(callbacks: VoiceModeCallbacks): void {
    this.callbacks = callbacks;
  }

  toggle(): void {
    if (this._state === 'off') {
      this.start();
    } else {
      this.exit('toggle');
    }
  }

  exit(
    reason: 'toggle' | 'typed' | 'error' | 'silence' | 'max-turns' = 'toggle'
  ): void {
    const cb = this.callbacks;
    if (!cb) return;

    // Set state to 'off' before stopping STT so the async onend callback
    // sees state='off' and handleSpeechEnd returns early instead of sending
    // the transcript as a chat message.
    const turnCount = this.turnCount;
    this.turnCount = 0;
    this.silenceStrikes = 0;
    this.setState('off');

    cb.stt.stopListening();
    this.speculative.abort();
    this.cancelReListenTimeout();
    this.stopMicStream();
    const audio = this.audioManager.getSnapshot();
    if (audio.isPlaying || audio.isLoading) {
      cb.analytics.trackEvent(AnalyticsEvent.TTS_PLAY_STOPPED);
    }
    this.audioManager.stop();

    cb.analytics.trackEvent(AnalyticsEvent.VOICE_MODE_ENDED, {
      reason,
      turnsCompleted: turnCount,
    });
  }

  handleSpeechEnd(transcript: string): void {
    if (this._state !== 'listening') return;

    if (!transcript.trim()) {
      this.handleSilence();
      return;
    }

    this.silenceStrikes = 0;
    this.stopMicStream();
    this.setState('processing');
    this.processTranscript(transcript);
  }

  /** Called at SPECULATIVE_SILENCE_MS (before the full silence threshold).
   *  Starts the backend fetch, consumes the SSE stream, and fires TTS
   *  requests eagerly so audio may be ready by the time we commit. */
  handleSpeculativeSilence(transcript: string): void {
    if (this._state !== 'listening') return;
    if (!transcript.trim()) return;
    if (this._isTtsMuted) return;

    const cb = this.callbacks;
    if (!cb?.tts.isEnabled) return;

    this.speculative.start(cb, transcript);
  }

  /** Called when speech resumes after a speculative silence was signaled. */
  handleSpeechResumed(): void {
    this.speculative.abort();
  }

  stopAllAudio(): void {
    this.audioManager.stop();
  }

  toggleTtsMute(): void {
    this._isTtsMuted = !this._isTtsMuted;

    if (this._isTtsMuted) {
      const audio = this.audioManager.getSnapshot();
      if (audio.isPlaying || audio.isLoading) {
        this.audioManager.stop();
      }
    }

    this.callbacks?.analytics.trackEvent(
      this._isTtsMuted
        ? AnalyticsEvent.TTS_MUTE_ENABLED
        : AnalyticsEvent.TTS_MUTE_DISABLED
    );
    this.emitSnapshot();
  }

  /** Re-subscribe after dispose() so voice mode survives StrictMode remounts. */
  reconnectAudio(): void {
    if (this.unsubscribeAudio) return;
    this.prevAudioState = this.audioManager.getSnapshot();
    this.unsubscribeAudio = this.audioManager.subscribe(() => {
      const next = this.audioManager.getSnapshot();
      const prev = this.prevAudioState;
      this.prevAudioState = next;
      this.handleAudioStateChange(prev, next);
    });
  }

  dispose(): void {
    this.speculative.abort();
    this.cancelReListenTimeout();
    this.stopMicStream();
    this.unsubscribeAudio?.();
    this.unsubscribeAudio = null;
    this.audioManager.stop();
    this._analyserService.dispose();
  }

  // -- Private --

  private start(): void {
    const cb = this.callbacks;
    if (!cb || !cb.tts.isEnabled) return;

    cb.analytics.trackEvent(AnalyticsEvent.VOICE_MODE_STARTED, {});
    this.silenceStrikes = 0;
    this.turnCount = 0;
    this._isTtsMuted = false;
    this.startListeningPhase();
  }

  private startListeningPhase(): void {
    this.setState('listening');
    this.callbacks?.stt.startListening({ continuous: true });
    this.openMicStream();
  }

  private async openMicStream(): Promise<void> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (this._state !== 'listening') {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      this.micStream = stream;
      await this._analyserService.connectMicStream(stream);
    } catch {
      // Mic access denied or unavailable - waveform won't animate, voice mode still works
    }
  }

  private stopMicStream(): void {
    if (this.micStream) {
      this.micStream.getTracks().forEach((t) => t.stop());
      this.micStream = null;
    }
    this._analyserService.disconnect();
  }

  private async processTranscript(transcript: string): Promise<void> {
    const cb = this.callbacks;
    if (!cb) return;

    try {
      if (cb.tts.isEnabled && !this._isTtsMuted) {
        await this.processTranscriptStreaming(cb, transcript);
        return;
      }

      // Non-streaming fallback when TTS is muted or unavailable
      const answerText = await this.fetchAnswer(cb, transcript);
      if (this._state !== 'processing') return;

      if (!answerText) {
        this.exit('error');
        return;
      }

      if (!cb.tts.isEnabled) {
        cb.analytics.trackEvent(AnalyticsEvent.VOICE_MODE_TTS_FALLBACK, {
          error: 'TTS not enabled',
        });
        this.exit('error');
        return;
      }

      this.playOrSkipTts(cb, answerText);
    } catch {
      if (this._state === 'processing' || this._state === 'speaking') {
        this.exit('error');
      }
    }
  }

  private async processTranscriptStreaming(
    cb: VoiceModeCallbacks,
    transcript: string
  ): Promise<void> {
    const committed = this.speculative.take(cb, transcript);

    if (committed) {
      cb.chat.displayVoiceExchange(transcript, committed.displayText);
      if (committed.metadata) {
        cb.chat.updateVoiceMetadata(committed.metadata);
      }

      this.trackTtsRequested(cb);

      await Promise.all([
        this.audioManager.playSequence(committed.audioSequence),
        committed.displayPromise,
      ]);
    } else {
      await this.runFreshStreamingPipeline(cb, transcript);
    }
  }

  /** Fallback path when no speculative pipeline exists. Runs the full
   *  stream -> sentence -> TTS -> playback flow from scratch. */
  private async runFreshStreamingPipeline(
    cb: VoiceModeCallbacks,
    transcript: string
  ): Promise<void> {
    const collectedChunks: string[] = [];
    cb.chat.displayVoiceExchange(transcript, '');

    const sentenceQueue = new SentenceQueue();
    const stream = cb.chat.streamVoiceSearch(transcript);

    const streamConsumer = consumeEventStream(stream, {
      onChunk: (chunk) => {
        collectedChunks.push(chunk);
        cb.chat.updateVoiceAnswer(
          `${collectedChunks.join('').replace(/\[\d+\]/g, '')} ...`
        );
      },
      onSentence: (sentence) => sentenceQueue.push(sentence),
      onMetadata: (metadata) => cb.chat.updateVoiceMetadata(metadata),
      onStreamEnd: () => {
        // Restore full text with [1] markers now that the stream is complete.
        // AssistantMessage needs markers in the text AND citations in state
        // to render styled citations.
        const fullAnswer = collectedChunks.join('');
        if (fullAnswer) {
          cb.chat.updateVoiceAnswer(fullAnswer);
        }
        // Always signal the queue so drainSentenceQueue stops waiting,
        // even if the stream threw. Without this, playSequence hangs forever.
        sentenceQueue.finish();
      },
    });

    this.trackTtsRequested(cb);

    const audioFetches = this.drainSentenceQueue(cb, sentenceQueue);

    await Promise.all([
      this.audioManager.playSequence(audioFetches),
      streamConsumer,
    ]);
  }

  /** Yields TTS fetch functions as sentences become available in the queue. */
  private async *drainSentenceQueue(
    cb: VoiceModeCallbacks,
    queue: SentenceQueue
  ): AsyncGenerator<() => Promise<Response>> {
    for await (const sentence of queue) {
      yield () => cb.tts.requestAudio(sentence);
    }
  }

  private trackTtsRequested(cb: VoiceModeCallbacks): void {
    cb.analytics.trackEvent(AnalyticsEvent.TTS_PLAY_REQUESTED, {
      textLengthChars: 0,
    });
    cb.analytics.createEvent('tts_requested', { textLengthChars: 0 });
  }

  private async fetchAnswer(
    cb: VoiceModeCallbacks,
    transcript: string
  ): Promise<string | undefined> {
    const response = await cb.chat.sendMessage({
      newMessage: transcript,
      filterGroups: cb.chat.getAppliedFilterGroups(),
    });
    return response?.answer;
  }

  private playOrSkipTts(cb: VoiceModeCallbacks, answerText: string): void {
    cb.analytics.trackEvent(AnalyticsEvent.TTS_PLAY_REQUESTED, {
      textLengthChars: answerText.length,
    });
    cb.analytics.createEvent('tts_requested', {
      textLengthChars: answerText.length,
    });

    if (this._isTtsMuted) {
      this.completeTurnAndReListen(cb, answerText.length);
      return;
    }

    this.audioManager.playFromFetch(() => cb.tts.requestAudio(answerText));
  }

  private completeTurnAndReListen(
    cb: VoiceModeCallbacks,
    textLengthChars: number
  ): void {
    cb.analytics.trackEvent(AnalyticsEvent.TTS_PLAY_MUTED_SKIP, {
      textLengthChars,
    });

    if (!this.advanceTurnOrExit(cb)) return;
    this.startListeningPhase();
  }

  /** Returns true if voice mode should continue, false if max-turns was reached and we exited. */
  private advanceTurnOrExit(cb: VoiceModeCallbacks): boolean {
    this.turnCount += 1;
    cb.analytics.trackEvent(AnalyticsEvent.VOICE_MODE_TURN_COMPLETED, {
      turnIndex: this.turnCount,
    });

    if (this.turnCount >= MAX_VOICE_TURNS) {
      cb.analytics.trackEvent(AnalyticsEvent.VOICE_MODE_MAX_TURNS_REACHED, {
        turnCount: this.turnCount,
      });
      this.exit('max-turns');
      return false;
    }
    return true;
  }

  private handleSilence(): void {
    this.silenceStrikes += 1;
    if (this.silenceStrikes >= MAX_SILENCE_STRIKES) {
      this.exit('silence');
      return;
    }
    this.callbacks?.stt.startListening({ continuous: true });
  }

  private handleAudioStateChange(
    prev: AudioPlaybackState,
    next: AudioPlaybackState
  ): void {
    this.trackTtsAnalytics(prev, next);

    // Only transition to speaking from processing state
    if (this._state === 'processing' && !prev.isPlaying && next.isPlaying) {
      this.setState('speaking');
    }

    // Handle TTS errors that occur during loading (before playback starts)
    if (this._state === 'processing' && !prev.error && next.error) {
      const cb = this.callbacks;
      if (cb) {
        cb.analytics.trackEvent(AnalyticsEvent.VOICE_MODE_TTS_FALLBACK, {
          error: next.error,
        });
        this.exit('error');
      }
      return;
    }

    this.handleTtsPlaybackFinished(prev, next);
  }

  private trackTtsAnalytics(
    prev: AudioPlaybackState,
    next: AudioPlaybackState
  ): void {
    const cb = this.callbacks;
    if (!cb) return;

    if (!prev.isPlaying && next.isPlaying) {
      cb.analytics.trackEvent(AnalyticsEvent.TTS_PLAY_STARTED);
    }
    if (prev.isPlaying && !next.isPlaying && !next.error && !next.isLoading) {
      cb.analytics.trackEvent(AnalyticsEvent.TTS_PLAY_COMPLETED);
    }
    if (!prev.error && next.error) {
      cb.analytics.trackEvent(AnalyticsEvent.TTS_PLAY_ERROR, {
        error: next.error,
      });
    }
  }

  private handleTtsPlaybackFinished(
    prev: AudioPlaybackState,
    next: AudioPlaybackState
  ): void {
    if (this._state !== 'speaking') return;
    if (!(prev.isPlaying || prev.isLoading)) return;
    if (next.isPlaying || next.isLoading) return;

    const cb = this.callbacks;
    if (!cb) return;

    if (next.error) {
      cb.analytics.trackEvent(AnalyticsEvent.VOICE_MODE_TTS_FALLBACK, {
        error: next.error,
      });
      this.exit('error');
      return;
    }

    if (!this.advanceTurnOrExit(cb)) return;

    this.reListenTimeoutId = setTimeout(() => {
      this.reListenTimeoutId = null;
      if (this._state !== 'speaking') return;
      this.startListeningPhase();
    }, POST_TTS_LISTEN_DELAY_MS);
  }

  private cancelReListenTimeout(): void {
    if (this.reListenTimeoutId !== null) {
      clearTimeout(this.reListenTimeoutId);
      this.reListenTimeoutId = null;
    }
  }

  private setState(newState: VoiceModeState): void {
    if (this._state === newState) return;
    this._state = newState;
    this.emitSnapshot();
  }

  private emitSnapshot(): void {
    const audio = this.audioManager.getSnapshot();
    this._snapshot = {
      voiceModeState: this._state,
      isTtsMuted: this._isTtsMuted,
      isReadingOut: audio.isPlaying,
    };
    this.notifyListeners();
  }

  private notifyListeners(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }
}
